import * as React from "react"
import { BillingInterval, MachinePricingTier } from "@modules/graphql/types"
import { planInformation as planInformationText } from "@modules/locales/default.json"
import { MdClose, MdEdit } from "react-icons/md"
import { DiDatabase } from "react-icons/di"
import { FormattedMessage, commonMessageTags } from "@modules/locales"
import {
  IconProps,
  FreePlanIcon,
  IndividualPlanIcon,
  TeamPlanIcon,
  EnterprisePlanIcon,
  HostingFreePlanIcon,
  HostingStandardPlanIcon,
  HostingPerformancePlanIcon,
  HostingEnterprisePlanIcon,
  DefaultSpeedIcon,
  FastSpeedIcon,
  FasterSpeedIcon,
  FastestSpeedIcon,
  OneUserIcon,
  ManyUsersIcon,
  UnlimitedUsersIcon,
  FreeBandwidthIcon,
  FreeRequestsIcon,
  StandardBandwidthIcon,
  StandardRequestsIcon,
  ProBandwidthIcon,
  ProRequestsIcon,
  getTheme,
} from "gatsby-interface"

const { colors } = getTheme()

export type PlanInfo = {
  planId: string | null
  planName: string | null
  billingInterval: BillingInterval
  buildsTier?: MachinePricingTier | null
  hostingTier?: MachinePricingTier | null
}

export enum SupportedTier {
  Free = `FREE`,
  Standard = `STANDARD`,
  Performance = `PERFORMANCE`,
  Enterprise = `ENTERPRISE`,
}

export type SupportedPricingTier =
  | MachinePricingTier.Free
  | MachinePricingTier.Standard
  | MachinePricingTier.Performance
  | MachinePricingTier.Enterprise

export type RichFeature = {
  icon?: (props: IconProps) => JSX.Element
  label: React.ReactNode
  negative?: boolean
}

export type TierType = `builds` | `hosting`

export type TierInformation = {
  key: SupportedTier
  pricingTier: SupportedPricingTier
  name: string
  color: string
  monthlyAmount?: Record<BillingInterval, number | null>
  buildsIcon?: (props: IconProps) => JSX.Element
  buildsMonthlyAmount?: Record<BillingInterval, number | null>
  buildsDescription?: string
  buildsFeaturesIntro?: string
  buildsFeatures?: RichFeature[]
  hostingIcon?: (props: IconProps) => JSX.Element
  hostingDescription?: string
  hostingFeaturesIntro?: string
  hostingFeatures?: RichFeature[]
  hostingMonthlyAmount?: Record<BillingInterval, number | null>
  cta?: string
}

export const supportedTiers: Omit<
  Record<MachinePricingTier, TierInformation>,
  "OPTIMIZED"
> = {
  FREE: {
    key: SupportedTier.Free,
    pricingTier: MachinePricingTier.Free,
    name: planInformationText.morphemes.freePlanName,
    color: colors.blue[80],
    buildsIcon: FreePlanIcon,
    buildsDescription: `For side projects and new ideas`,
    buildsFeatures: [
      {
        icon: OneUserIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> user"
            values={{
              value: "1",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        icon: DefaultSpeedIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Build speed"
            values={{
              value: "Default",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        icon: MdClose,
        label: `Incremental Builds`,
        negative: true,
      },
      {
        label: `Previews`,
      },
    ],
    hostingIcon: HostingFreePlanIcon,
    hostingDescription: `For side projects and new ideas`,
    hostingFeatures: [
      {
        icon: FreeBandwidthIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Bandwidth"
            values={{
              value: "100GB",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        icon: FreeRequestsIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Requests"
            values={{
              value: "1M",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        label: `Free SSL Certificates`,
      },
      {
        label: `Custom domains`,
      },
    ],
    buildsMonthlyAmount: {
      MONTHLY: 0,
      ANNUAL: 0,
    },
    hostingMonthlyAmount: {
      MONTHLY: 0,
      ANNUAL: 0,
    },
  },

  STANDARD: {
    key: SupportedTier.Standard,
    pricingTier: MachinePricingTier.Standard,
    name: planInformationText.morphemes.standardPlanName,
    color: colors.red[80],
    buildsIcon: IndividualPlanIcon,
    buildsDescription: `For sites and apps that are gaining traction`,
    buildsFeatures: [
      {
        icon: ManyUsersIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> users"
            values={{
              value: "10",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        icon: FastSpeedIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Build speed"
            values={{
              value: "Fast",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        label: `Incremental Builds`,
      },
      {
        label: `Previews`,
      },
    ],
    hostingIcon: HostingStandardPlanIcon,
    hostingDescription: `For sites and apps that are gaining traction`,
    hostingFeatures: [
      {
        icon: StandardBandwidthIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Bandwidth"
            values={{
              value: "300GB",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        icon: StandardRequestsIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Requests"
            values={{
              value: "30M",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        label: `Free SSL Certificates`,
      },
      {
        label: `Custom domains`,
      },
    ],
    buildsMonthlyAmount: {
      MONTHLY: 2000,
      ANNUAL: 20400 / 12,
    },
    hostingMonthlyAmount: {
      MONTHLY: 2000,
      ANNUAL: 20400 / 12,
    },
  },

  PERFORMANCE: {
    key: SupportedTier.Performance,
    pricingTier: MachinePricingTier.Performance,
    name: planInformationText.morphemes.performancePlanName,
    color: colors.magenta[60],
    buildsIcon: TeamPlanIcon,
    buildsDescription: `For large teams and projects with a need for speed`,
    buildsFeatures: [
      {
        icon: UnlimitedUsersIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> users"
            values={{
              value: "Unlimited",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        icon: FasterSpeedIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Build speed"
            values={{
              value: "Faster",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        label: `Incremental Builds`,
      },
      {
        label: `Previews`,
      },
    ],
    hostingIcon: HostingPerformancePlanIcon,
    hostingDescription: `For large teams and projects with a need for speed`,
    hostingFeatures: [
      {
        icon: ProBandwidthIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Bandwidth"
            values={{
              value: "1TB",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        icon: ProRequestsIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Requests"
            values={{
              value: "80M",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        label: `Free SSL Certificates`,
      },
      {
        label: `Custom domains`,
      },
    ],
    buildsMonthlyAmount: {
      MONTHLY: 10000,
      ANNUAL: 102000 / 12,
    },
    hostingMonthlyAmount: {
      MONTHLY: 10000,
      ANNUAL: 102000 / 12,
    },
  },
  ENTERPRISE: {
    key: SupportedTier.Enterprise,
    pricingTier: MachinePricingTier.Enterprise,
    name: planInformationText.morphemes.enterprisePlanName,
    color: colors.purple[60],
    buildsIcon: EnterprisePlanIcon,
    buildsDescription: `Customized to your needs`,
    buildsFeaturesIntro: `Get all Builds features, plus:`,
    buildsFeatures: [
      {
        icon: UnlimitedUsersIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> users"
            values={{
              value: "Unlimited",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        icon: FastestSpeedIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Build speed"
            values={{
              value: "Fastest",
            }}
            tags={commonMessageTags}
          />
        ),
      },
    ],
    hostingIcon: HostingEnterprisePlanIcon,
    hostingDescription: `Customized to your needs`,
    hostingFeaturesIntro: `Get all Hosting features, plus:`,
    hostingFeatures: [
      {
        icon: DiDatabase,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Bandwidth"
            values={{
              value: "Custom",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        icon: MdEdit,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Requests"
            values={{
              value: "Custom",
            }}
            tags={commonMessageTags}
          />
        ),
      },
    ],
    buildsMonthlyAmount: {
      MONTHLY: null,
      ANNUAL: null,
    },
    hostingMonthlyAmount: {
      MONTHLY: null,
      ANNUAL: null,
    },
    cta: planInformationText.actions.pickTier4,
  },
}
